import { z } from 'zod';

import { doc, documentedStringEnum, itemCountField, isoDateTimeField, moneyField, optionalDocumentedStringEnum, suggestedStringValues } from './openapiHelpers';
import {
    DRIVER_COMPANY_VALUES,
    INCOTERMS_VALUES,
    LINE_STATE_VALUES,
    RECEIVING_PRIORITY_VALUES,
    RECEIVING_STATUS_VALUES,
    RECEIVING_TYPE_VALUES,
    RECEIVING_URGENT_VALUES,
} from '../fieldEnums';

/** Po Joi validaci na GWMS route může být datum `Date` místo ISO řetězce z JSON. */
const joiDateOrString = (schema: z.ZodType<string>) =>
    z.preprocess((val) => {
        if (val instanceof Date && !Number.isNaN(val.getTime())) {
            return val.toISOString();
        }
        return val;
    }, schema);

const joiDateOrStringNullish = () =>
    z.preprocess((val) => {
        if (val === null || val === undefined || val === '') {
            return val;
        }
        if (val instanceof Date && !Number.isNaN(val.getTime())) {
            return val.toISOString();
        }
        return val;
    }, z.string().nullish());

const deliveryTypeEnum = documentedStringEnum(
    ['courier', 'transportCompany', 'truckingCompany', 'container'],
    'How the goods arrive at the warehouse. Value containter is rejected — use container.',
);

const driverCompanyField = suggestedStringValues(
    DRIVER_COMPANY_VALUES,
    'Carrier delivering the receiving (name from the MySkladon carrier list). When other, driverCompanyOtherName is required.',
    200,
);

const receivingProductSchema = z.object({
    sku: doc(z.string().min(1), 'Product SKU on the receiving line.'),
    quantity: itemCountField(),
    lot: doc(z.string().nullish(), 'Expected lot/batch.'),
    expirationDate: joiDateOrStringNullish(),
    productPrice: moneyField(),
    lineState: documentedStringEnum(
        LINE_STATE_VALUES,
        'Line state. E = enabled, D = disabled (omitted lines on update are marked D). If omitted on create, defaults to E.',
    ).optional(),
}).strict();

const receivingProductResponseSchema = receivingProductSchema.extend({
    received: doc(z.number().int().nullish(), 'Quantity received for this line.'),
});

const fileCreatorRoleEnum = documentedStringEnum(
    ['skladon_employee', 'client'],
    'Who uploaded the file.',
);

const receivingFileSchema = z.object({
    id: doc(z.number().int().optional(), 'File ID when updating an existing attachment.'),
    type: doc(z.string().min(1).default('other'), 'File category. If omitted, defaults to other.'),
    data: doc(z.string().optional(), 'Base64 content. Required for new files.'),
    fileName: doc(z.string().min(1), 'Original file name.'),
    fileType: doc(z.string().min(1), 'MIME type or extension.'),
    fileSize: doc(z.string().optional(), 'File size.'),
    description: doc(z.string().max(500).nullish(), 'Human-readable file description.'),
    creatorRole: fileCreatorRoleEnum.nullish(),
    createdAt: doc(z.union([z.string(), z.date()]).nullish(), 'Upload timestamp from files.CREATED_AT.'),
}).strict();

const receivingV21BodyFields = {
    description: doc(z.string().nullish(), 'Receiving description / note.'),
    trackingNumber: doc(z.string().nullish(), 'Inbound shipment tracking number.'),
    supplier: doc(z.string().nullish(), 'Supplier name.'),
    estimatedDelivery: doc(joiDateOrString(z.string().min(1)), 'Expected delivery datetime (ISO).'),
    deliveryNoteNumber: doc(z.string().nullish(), 'Delivery note / DN number.'),
    deliveryType: deliveryTypeEnum.nullish(),
    duty: doc(z.boolean().optional(), 'Goods held in customs duty process.'),
    carrierInfo: doc(z.string().nullish(), 'Free-text carrier information.'),
    handlingUnits: doc(z.string().nullish(), 'Handling units description.'),
    urgent: optionalDocumentedStringEnum(RECEIVING_URGENT_VALUES, 'Urgent handling flag from warehouse workflow.'),
    unitsAmount: doc(z.number().int(), 'Number of handling units (pallets/cartons) announced.'),
    driverCompany: driverCompanyField.nullish(),
    driverCompanyOtherName: doc(
        z.string().nullish(),
        'Required when driverCompany is other — raw carrier name.',
    ),
    driverTrailerLicensePlate: doc(z.string().nullish(), 'Trailer license plate.'),
    driverName: doc(z.string().nullish(), 'Driver name.'),
    driverPhone: doc(z.string().nullish(), 'Driver phone number.'),
    driverDispatcherContact: doc(z.string().nullish(), 'Dispatcher contact.'),
    eshopNote: doc(z.string().nullish(), 'Your internal note stored on the receiving.'),
    priority: z.number().int().optional().refine(
        (value) => value === undefined || (RECEIVING_PRIORITY_VALUES as readonly number[]).includes(value),
        { message: `Allowed values: ${RECEIVING_PRIORITY_VALUES.join(', ')}` },
    ).describe(`Expedition priority. 0 = normal, 1 = high. If omitted on create, defaults to 0. Allowed values: ${RECEIVING_PRIORITY_VALUES.join(', ')}.`),
    containerNumber: doc(z.string().nullish(), 'Container number for container deliveries.'),
    announcedPalletsCount: doc(z.number().int().nullish(), 'Number of pallets announced.'),
    receivingType: optionalDocumentedStringEnum(RECEIVING_TYPE_VALUES, 'Physical layout of the inbound shipment.'),
    incoterms: optionalDocumentedStringEnum(INCOTERMS_VALUES, 'Delivery terms (Incoterms).'),
    products: doc(z.array(receivingProductSchema), 'Expected products on the receiving.'),
    files: doc(z.array(receivingFileSchema).optional(), 'Attachments (delivery notes, photos, …).'),
} as const;

const driverCompanyOtherRefinement = (
    data: { driverCompany?: string | null; driverCompanyOtherName?: string | null },
) => {
    if (data.driverCompany === 'other') {
        return typeof data.driverCompanyOtherName === 'string' && data.driverCompanyOtherName.length > 0;
    }
    return true;
};

const driverCompanyOtherMessage = {
    message: 'driverCompanyOtherName is required when driverCompany is "other"',
    path: ['driverCompanyOtherName'],
};

export const createReceivingV21Schema = z.object({
    eshopReceivingId: doc(z.string().min(1), 'Your receiving (ASN) identifier — unique within the eshop.'),
    ...receivingV21BodyFields,
}).strict().refine(driverCompanyOtherRefinement, driverCompanyOtherMessage);

export const updateReceivingV21Schema = z.object({
    /** Volitelné v body kvůli kompatibilitě s v2; autoritativní hodnota je v URL path. */
    eshopReceivingId: doc(
        z.string().min(1).optional(),
        'Optional in body for v2 compatibility; the authoritative value is the URL path parameter.',
    ),
    ...receivingV21BodyFields,
}).strict().refine(driverCompanyOtherRefinement, driverCompanyOtherMessage);

/** PATCH – same as GWMS v2 `patchReceiving`. */
const patchReceivingStatusSchema = documentedStringEnum(['canceled'], 'Status allowed on PATCH.');

export const patchReceivingV21Schema = z.object({
    eshopReceivingId: doc(
        z.string().min(1).optional(),
        'Optional in body for v2 compatibility; the authoritative value is the URL path parameter.',
    ),
    ...receivingV21BodyFields,
    status: patchReceivingStatusSchema.optional(),
}).partial().strict().refine(
    (data) => Object.keys(data).length > 0,
    { message: 'At least one field must be provided' },
).refine(driverCompanyOtherRefinement, driverCompanyOtherMessage);

const receivingStatusEnum = documentedStringEnum(
    RECEIVING_STATUS_VALUES,
    'Receiving status in Skladon workflow.',
);

const receivingV21ResponseReadOnly = {
    id: doc(z.number().int(), 'Internal Skladon receiving ID.'),
    asn: doc(z.string().optional(), 'Skladon ASN number assigned to the receiving.'),
    createdAt: doc(z.string().optional(), 'Record creation timestamp.'),
    slaDate: doc(z.string().nullish(), 'SLA deadline for receiving processing.'),
    quantity: doc(z.number().nullish(), 'Announced quantity of items.'),
    received: doc(z.number().nullish(), 'Quantity already received.'),
    diffQuantity: doc(z.number().nullish(), 'Difference between announced and received quantity.'),
    asnLabelEmail: doc(z.string().nullish(), 'E-mail used for ASN label delivery.'),
    status: receivingStatusEnum.nullish(),
    realDelivery: doc(z.string().nullish(), 'Actual delivery datetime when goods arrived.'),
    slaSuspended: doc(z.string().nullish(), 'When SLA tracking was suspended.'),
    photoDocumentationTaken: doc(z.string().nullish(), 'When photo documentation was taken.'),
    deliveredPalletsCount: doc(z.number().int().nullish(), 'Number of pallets delivered.'),
    deliveredCartonsCount: doc(z.number().int().nullish(), 'Number of cartons delivered.'),
    returnedEurPalletsCount: doc(z.number().int().nullish(), 'Number of EUR pallets returned to carrier.'),
    deliveredStandardPalletsCount: doc(z.number().int().nullish(), 'Number of standard pallets delivered.'),
    returnedStandardPalletsCount: doc(z.number().int().nullish(), 'Number of standard pallets returned.'),
    deliveredOtherPalletsCount: doc(z.number().int().nullish(), 'Number of other pallet types delivered.'),
    deliveredBrokenPalletsCount: doc(z.number().int().nullish(), 'Number of broken pallets delivered.'),
    skladonNote: doc(z.string().nullish(), 'Internal Skladon note (read-only).'),
    unloadedAt: isoDateTimeField('Timestamp when the receiving was unloaded.'),
    receivedAt: isoDateTimeField('Timestamp when the receiving was fully received.'),
    confirmedAt: isoDateTimeField('Timestamp when the receiving was confirmed.'),
    canceledAt: isoDateTimeField('Timestamp when the receiving was canceled.'),
} as const;

export const receivingV21ResponseSchema = z.object({
    eshopReceivingId: doc(z.string(), 'Your receiving (ASN) identifier.'),
    ...receivingV21BodyFields,
    products: doc(z.array(receivingProductResponseSchema), 'Products on the receiving including received quantities.'),
    ...receivingV21ResponseReadOnly,
});

export type CreateReceivingV21 = z.infer<typeof createReceivingV21Schema>;
export type UpdateReceivingV21 = z.infer<typeof updateReceivingV21Schema>;
export type PatchReceivingV21 = z.infer<typeof patchReceivingV21Schema>;
export type ReceivingV21 = z.infer<typeof receivingV21ResponseSchema>;
